import { memo, useDeferredValue, useRef, type RefObject } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, ChevronUp, Copy, ExternalLink, Loader2, Pencil, Shield, Users } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
  Tooltip, TooltipContent, TooltipTrigger,
} from "@nexus/ui";
import { cn } from "@nexus/ui";
import { useIsOwnAccessGroup } from "@/features/auth/hooks/usePermissions";
import { tenantAccentColor } from "@/features/identity-hub/utils/iam.utils";
import { timeAgo } from "@/features/identity-hub/utils/time";
import {
  orgAccentTableCellWashStyle,
  orgAccentTableCellWashStyleDark,
  orgAccentVar,
} from "@/features/identity-hub/utils/org-accent-surface";
import { ImagePreviewTrigger } from "./ImagePreviewTrigger";
import { OrgLogoMark } from "./OrgLogoMark";
import { ArchivedBadge, RoleTypeBadge, RoleTypeIcon, YourRoleBadge } from "./shared/RoleBadges";
import { TableEmptyState } from "./shared/TableEmptyState";
import { RowActionsTrigger } from "./shared/RowActionsTrigger";
import { SortHeader } from "./shared/table/SortHeader";
import { useTableVirtualizer } from "./shared/table/useTableVirtualizer";
import { useRowFlash } from "./shared/table/useRowFlash";
import { ROW_FLASH_STYLE_TAG } from "./shared/table/rowFlashStyle";

export type RoleSortKey = "name" | "description" | "tenantName" | "memberCount" | "permissionCount" | "type" | "updatedAt";

export interface RoleSortState {
  key: RoleSortKey;
  dir: "asc" | "desc";
}

export interface RoleRow {
  id: string;
  name: string;
  description: string | null;
  tenantId: string;
  tenantName: string;
  tenantType: "API" | "ORGANIZATION" | "SUPER_TENANT";
  tenantLogoUrl?: string | null;
  tenantBrandColor?: string | null;
  memberCount: number;
  permissionCount: number;
  type: "system" | "custom";
  updatedAt: string;
  /** True when the role itself or its parent org is soft-deleted. */
  isArchived: boolean;
}

function OrgTableCell({
  name,
  type,
  logoUrl,
  brandColor,
}: {
  name: string;
  type: "API" | "ORGANIZATION" | "SUPER_TENANT";
  logoUrl?: string | null;
  brandColor?: string | null;
}) {
  const isSuperTenant = type === "SUPER_TENANT";
  const accent = tenantAccentColor({ name, brandColor });

  return (
    <td
      className="relative px-3 py-3"
      style={isSuperTenant ? undefined : orgAccentVar(accent)}
    >
      {isSuperTenant ? (
        <>
          <div
            aria-hidden
            className="iam-super-tenant-card__mesh pointer-events-none absolute inset-0 motion-reduce:opacity-80"
          />
          <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden motion-reduce:hidden">
            <div className="iam-super-tenant-card__shimmer absolute inset-y-0 -left-1/2 w-1/2" />
          </div>
        </>
      ) : (
        <>
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 dark:hidden"
            style={orgAccentTableCellWashStyle(accent)}
          />
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 hidden dark:block"
            style={orgAccentTableCellWashStyleDark(accent)}
          />
        </>
      )}
      <ImagePreviewTrigger src={logoUrl} alt={name}>
        <div className="relative z-[1] flex min-w-0 items-center gap-1.5">
          <OrgLogoMark name={name} logoUrl={logoUrl} accent={accent} isSuperTenant={isSuperTenant} size={20} />
          <span className="truncate text-sm text-foreground">{name}</span>
        </div>
      </ImagePreviewTrigger>
    </td>
  );
}

function EditPermissionsMenuItem({
  row,
  onEditPermissions,
}: {
  row: RoleRow;
  onEditPermissions: (role: RoleRow) => void;
}) {
  const { t } = useTranslation();
  const ta = (key: string) => t(`iam.dashboard.roles.table.actions.${key}`);
  const isOwnAccessGroup = useIsOwnAccessGroup(row.id);
  const isDisabled = row.isArchived || isOwnAccessGroup;

  const disabledReason = isOwnAccessGroup
    ? ta("editPermissionsSelfLockedTip")
    : row.isArchived
      ? ta("editPermissionsArchivedTip")
      : null;

  const item = (
    <DropdownMenuItem
      className="gap-2"
      disabled={isDisabled}
      onClick={() => onEditPermissions(row)}
    >
      <Shield className="h-3.5 w-3.5" />
      {ta("editPermissions")}
    </DropdownMenuItem>
  );

  if (!disabledReason) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>{item}</TooltipTrigger>
        <TooltipContent side="left" className="max-w-[220px]">
          {ta("editPermissionsTip")}
        </TooltipContent>
      </Tooltip>
    );
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="block w-full cursor-not-allowed">{item}</span>
      </TooltipTrigger>
      <TooltipContent side="left" className="max-w-[220px]">
        {disabledReason}
      </TooltipContent>
    </Tooltip>
  );
}

function DeleteRoleMenuItem({
  row,
  onDeleteRole,
}: {
  row: RoleRow;
  onDeleteRole?: (role: RoleRow) => void;
}) {
  const { t } = useTranslation();
  const isSystemRole = row.type === "system";
  const lacksPermission = !onDeleteRole;
  const isDisabled = isSystemRole || lacksPermission;

  const disabledReason = isSystemRole
    ? t("iam.rolesTable.deleteDisabledSystem", "Built-in system roles can't be deleted")
    : lacksPermission
      ? t("iam.rolesTable.deleteDisabledPermission", "You don't have permission to delete roles")
      : null;

  const item = (
    <DropdownMenuItem
      variant="destructive"
      icon="trash-2"
      disabled={isDisabled}
      onClick={() => onDeleteRole?.(row)}
    >
      {t("iam.dashboard.roles.table.actions.deleteRole", "Delete role")}
    </DropdownMenuItem>
  );

  if (!disabledReason) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>{item}</TooltipTrigger>
        <TooltipContent side="left" className="max-w-[220px]">
          {t(
            "iam.rolesTable.deleteTooltip",
            "Permanently delete this role and revoke it from all members. This can't be undone.",
          )}
        </TooltipContent>
      </Tooltip>
    );
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="block w-full cursor-not-allowed">{item}</span>
      </TooltipTrigger>
      <TooltipContent side="left">{disabledReason}</TooltipContent>
    </Tooltip>
  );
}

// ─── RoleRowImpl (memoized) ──────────────────────────────────────────
//
// Like PeopleTable, the row is memoized on primitive identity props so
// React.memo can skip rows whose flash/active state hasn't changed.
// Without this, virtualization helps DOM size but every dashboard state
// change still re-renders the visible window of rows including their
// Radix DropdownMenu roots.

interface RoleRowProps {
  row: RoleRow;
  isActive: boolean;
  onRowClick?: (id: string) => void;
  onEditPermissions?: (role: RoleRow) => void;
  onDeleteRole?: (role: RoleRow) => void;
  onOpenAuditLog?: (role: RoleRow) => void;
}

function RoleRowImpl({ row, isActive, onRowClick, onEditPermissions, onDeleteRole, onOpenAuditLog }: RoleRowProps) {
  const { t } = useTranslation();
  const ta = (key: string) => t(`iam.dashboard.roles.table.actions.${key}`);
  const isOwnAccessGroup = useIsOwnAccessGroup(row.id);
  return (
    <tr
      data-rowid={row.id}
      onClick={() => onRowClick?.(row.id)}
      className={cn(
        "group cursor-pointer transition-colors",
        isActive ? "bg-primary/5 hover:bg-primary/5" : "hover:bg-muted/40",
        row.isArchived && "opacity-60",
      )}
    >
      {/* Role name */}
      <td className="px-3 py-3">
        <div className="flex min-w-0 items-center gap-2.5">
          <RoleTypeIcon type={row.type} />
          <span
            className={cn(
              "truncate font-medium text-foreground",
              row.isArchived && "line-through decoration-muted-foreground/60",
            )}
          >
            {row.name}
          </span>
          {row.isArchived && <ArchivedBadge variant="outline" />}
          {isOwnAccessGroup && <YourRoleBadge />}
        </div>
      </td>

      {/* Description */}
      <td className="max-w-[220px] px-3 py-3">
        {row.description ? (
          <span className="line-clamp-2 text-xs text-muted-foreground">{row.description}</span>
        ) : (
          <span className="text-xs text-muted-foreground/40">—</span>
        )}
      </td>

      {/* Org */}
      <OrgTableCell
        name={row.tenantName}
        type={row.tenantType}
        logoUrl={row.tenantLogoUrl}
        brandColor={row.tenantBrandColor}
      />

      {/* Members */}
      <td className="px-3 py-3">
        <span className="tabular-nums text-foreground">{row.memberCount}</span>
      </td>

      {/* Permissions */}
      <td className="px-3 py-3">
        {row.type === "system" && row.name === "Super Admin" ? (
          <span className="text-base font-semibold text-foreground">∞</span>
        ) : (
          <span className="tabular-nums text-foreground">{row.permissionCount}</span>
        )}
      </td>

      {/* Type */}
      <td className="px-3 py-3">
        <RoleTypeBadge type={row.type} />
      </td>

      {/* Updated */}
      <td className="px-3 py-3">
        <span className="text-xs tabular-nums text-muted-foreground" title={row.updatedAt}>
          {timeAgo(row.updatedAt)}
        </span>
      </td>

      {/* Actions */}
      <td className="px-2 py-3" onClick={(e) => e.stopPropagation()}>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <RowActionsTrigger variant="hover-group" />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-44">
            <Tooltip>
              <TooltipTrigger asChild>
                <DropdownMenuItem
                  className="gap-2"
                  onClick={() => onRowClick?.(row.id)}
                >
                  <Pencil className="h-3.5 w-3.5" />
                  {ta("editRole")}
                </DropdownMenuItem>
              </TooltipTrigger>
              <TooltipContent side="left" className="max-w-[220px]">
                {ta("editRoleTip")}
              </TooltipContent>
            </Tooltip>
            {onEditPermissions && (
              <EditPermissionsMenuItem row={row} onEditPermissions={onEditPermissions} />
            )}
            <Tooltip>
              <TooltipTrigger asChild>
                <DropdownMenuItem
                  className="gap-2"
                  onClick={() => navigator.clipboard.writeText(row.id)}
                >
                  <Copy className="h-3.5 w-3.5" />
                  {ta("copyId")}
                </DropdownMenuItem>
              </TooltipTrigger>
              <TooltipContent side="left" className="max-w-[220px]">
                {ta("copyIdTip")}
              </TooltipContent>
            </Tooltip>
            {onOpenAuditLog && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <DropdownMenuItem
                    className="gap-2"
                    onClick={() => onOpenAuditLog(row)}
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                    {ta("viewAuditLog")}
                  </DropdownMenuItem>
                </TooltipTrigger>
                <TooltipContent side="left" className="max-w-[220px]">
                  {ta("viewAuditLogTip")}
                </TooltipContent>
              </Tooltip>
            )}
            <DropdownMenuSeparator />
            <DeleteRoleMenuItem row={row} onDeleteRole={onDeleteRole} />
          </DropdownMenuContent>
        </DropdownMenu>
      </td>
    </tr>
  );
}

const RoleRowMemo = memo(RoleRowImpl);

// ─── RolesTable ──────────────────────────────────────────────────────

interface RolesTableProps {
  rows: RoleRow[];
  sort: RoleSortState;
  onSort: (key: RoleSortKey) => void;
  activeId?: string | null;
  onRowClick?: (id: string) => void;
  /** When defined, renders an "Edit permissions" item in each row's "..." menu. */
  onEditPermissions?: (role: RoleRow) => void;
  onDeleteRole?: (role: RoleRow) => void;
  /** When defined, renders a "View audit log" item in each row's "..." menu. */
  onOpenAuditLog?: (role: RoleRow) => void;
  flashId?: string | null;
  /**
   * Vertical scroll container for row virtualization. The dashboard owns
   * the scroller (the `overflow-y-auto` wrapper around the tab content)
   * and passes its ref down — only the visible window of rows mounts,
   * regardless of how many roles a tenant has.
   */
  scrollContainerRef?: RefObject<HTMLElement | null>;
  hasMore?: boolean;
  isFetchingNextPage?: boolean;
  totalCount?: number;
}

const TABLE_COLUMN_COUNT = 8;

export function RolesTable({ rows, sort, onSort, activeId, onRowClick, onEditPermissions, onDeleteRole, onOpenAuditLog, flashId, scrollContainerRef, hasMore = false, isFetchingNextPage = false, totalCount }: RolesTableProps) {
  const { t } = useTranslation();
  const tc = (key: string) => t(`iam.dashboard.roles.table.columns.${key}`);
  const deferredRows = useDeferredValue(rows);
  const tableRef = useRef<HTMLTableElement>(null);

  // Match PeopleTable: virtualize against the parent tab's scroller so
  // hundreds of roles render without mounting hundreds of `<tr>`s + Radix
  // DropdownMenu roots. Falls back to no-op virtualization when no ref is
  // provided (consumers that pass a small `rows` list can omit it).
  const { virtualizer, virtualItems, topPad, bottomPad } = useTableVirtualizer({
    count: deferredRows.length,
    scrollContainerRef,
  });

  useRowFlash({ flashId, rows: deferredRows, virtualizer, tableRef });

  if (deferredRows.length === 0 && !isFetchingNextPage) {
    return (
      <TableEmptyState
        icon={Users}
        title={t("iam.dashboard.roles.table.empty.title", "No roles match your filters")}
        subtitle={t("iam.dashboard.roles.table.empty.subtitle", "Try clearing the filters or broadening your search.")}
      />
    );
  }

  return (
    <div className="overflow-hidden rounded-xl border border-border bg-card">
      <style>{ROW_FLASH_STYLE_TAG}</style>
      <div className="overflow-x-auto">
        <table ref={tableRef} className="w-full min-w-[880px] table-fixed text-sm">
          <colgroup>
            <col className="w-[22%]" />
            <col className="w-[24%]" />
            <col className="w-[18%]" />
            <col className="w-[104px]" />
            <col className="w-[116px]" />
            <col className="w-[92px]" />
            <col className="w-[104px]" />
            <col className="w-10" />
          </colgroup>
          <thead className="sticky top-0 z-10 bg-card">
            <tr className="group border-b border-border bg-muted/30 text-left text-xs font-medium text-muted-foreground">
              <SortHeader id="name"             label={tc("role")}         sort={sort} onSort={onSort} />
              <SortHeader id="description"      label={tc("description")}  sort={sort} onSort={onSort} />
              <SortHeader id="tenantName"       label={tc("org")}          sort={sort} onSort={onSort} />
              <SortHeader id="memberCount"      label={tc("members")}      sort={sort} onSort={onSort} />
              <SortHeader id="permissionCount"  label={tc("permissions")}  sort={sort} onSort={onSort} />
              <SortHeader id="type"             label={tc("type")}         sort={sort} onSort={onSort} />
              <SortHeader id="updatedAt"        label={tc("updated")}      sort={sort} onSort={onSort} />
              <th className="w-10" />
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {topPad > 0 && (
              <tr aria-hidden style={{ height: topPad }}>
                <td colSpan={TABLE_COLUMN_COUNT} style={{ padding: 0, border: 0 }} />
              </tr>
            )}
            {virtualItems.map((vi) => {
              const row = deferredRows[vi.index];
              if (!row) return null;
              return (
                <RoleRowMemo
                  key={row.id}
                  row={row}
                  isActive={activeId === row.id}
                  onRowClick={onRowClick}
                  onEditPermissions={onEditPermissions}
                  onDeleteRole={onDeleteRole}
                  onOpenAuditLog={onOpenAuditLog}
                />
              );
            })}
            {bottomPad > 0 && (
              <tr aria-hidden style={{ height: bottomPad }}>
                <td colSpan={TABLE_COLUMN_COUNT} style={{ padding: 0, border: 0 }} />
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between border-t border-border px-4 py-2.5 text-xs text-muted-foreground">
        <span>
          {totalCount != null
            ? t("iam.dashboard.roles.table.footer.showingCountOfTotal", { count: deferredRows.length, total: totalCount })
            : t("iam.dashboard.roles.table.footer.showingCount", { count: deferredRows.length })}
          {hasMore ? t("iam.dashboard.roles.table.footer.scrollForMore", " (scroll for more)") : ""}
        </span>
        {isFetchingNextPage && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
      </div>
    </div>
  );
}
